import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Badge, BadgeVariant } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Input } from '@heartinternet/hi-ui';
import { Textarea } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';
import { Tooltip } from '@heartinternet/hi-ui';
import { Form } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import { ConfirmationDialog } from '@heartinternet/hi-ui';
import { DescriptionList, DescriptionListItem } from '@heartinternet/hi-ui';
import './TicketViewPage.css';

export interface TicketUpdateAttachment {
  filename: string;
  originalFilename: string;
  stored: boolean;
  downloadUrl: string;
}

export interface TicketUpdateQuote {
  amount: number;
  work: string;
  paid?: boolean;
  aborted?: boolean;
  acceptUrl?: string;
}

export interface TicketUpdate {
  id: string;
  datetime: string;
  updateType: 'external' | 'internal';
  text?: string;
  textHtml?: string;
  customerId?: number;
  customerProxy?: string;
  updaterName?: string;
  status?: string;
  updatedNow?: boolean;
  rateable?: boolean;
  feedbackQuality?: number;
  salesSignatureText?: string;
  quote?: TicketUpdateQuote;
  expertServices?: string[];
  attachment?: TicketUpdateAttachment;
}

export interface TicketViewPageProps {
  /** Ticket ID - undefined means ticket not found */
  id?: string;
  /** Ticket subject */
  title?: string;
  /** Customer-facing status */
  status?: string;
  /** ISO8601 creation date */
  created?: string;
  /** ISO8601 last update date */
  updated?: string;
  /** Service description fallback */
  serviceDescription?: string;
  /** Service labels (preferred over serviceDescription) */
  subjectLabels?: string[];
  /** Customer name for proxy field default */
  customerName?: string;
  /** All ticket updates */
  updates?: TicketUpdate[];
  /** Rating level labels (index 0=N/A, 1=Very poor ... 9=Very good) */
  feedbackQualityLevels?: string[];
  /** AJAX feedback submission handler */
  onSubmitFeedback?: (updateId: string, rating: number) => Promise<boolean>;
  /** Base URL for form action */
  ticketUrl?: string;
  /** Pre-fetched glossary definitions keyed by term */
  glossaryDefinitions?: Record<string, string>;
  /** Custom CSS class */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
}

const DEFAULT_FEEDBACK_LEVELS = [
  'N/A',
  'Very poor',
  'Poor',
  'Quite poor',
  'Below average',
  'Average',
  'Above average',
  'Quite good',
  'Good',
  'Very good',
];

const STATUS_BADGE: Record<string, BadgeVariant> = {
  'AWAITING-SUPPORT': 'info',
  'AWAITING-CUSTOMER': 'warning',
  CLOSED: 'neutral',
  'CLOSED-LOCKED': 'neutral',
  URGENT: 'error',
};

const BATCH_SIZE = 10;

const ALLOWED_FILE_PATTERN =
  /^(?!.*\.[a-z]{1,4}\.)(?!.*\.\.)(?!.*[^a-z0-9_\s.-]).*[^\s.]+\.(jpe?g|png|txt|log|docx?|pdf)$/i;

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
    /* v8 ignore start */
  } catch {
    return iso;
  }
  /* v8 ignore stop */
}

let parseKeyCounter = 0;

function parseHtmlWithGlossary(html: string, definitions: Record<string, string>): React.ReactNode {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstChild;
  /* v8 ignore start */
  if (!root) return null;
  /* v8 ignore stop */

  parseKeyCounter = 0;

  function convertNode(node: Node): React.ReactNode {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const el = node as globalThis.Element;
    const tag = el.tagName.toLowerCase();
    const key = parseKeyCounter++;

    const children: React.ReactNode[] = [];
    el.childNodes.forEach((child) => {
      const converted = convertNode(child);
      if (converted !== null) children.push(converted);
    });

    // Glossary span → wrap in Tooltip
    if (tag === 'span' && el.classList.contains('glossary_highlighted')) {
      const term = el.getAttribute('data-term');
      const definition = term ? definitions[term] : undefined;
      if (definition) {
        return (
          <Tooltip key={key} content={definition} position="top">
            <span className="glossary_highlighted" data-term={term}>
              {children}
            </span>
          </Tooltip>
        );
      }
      return (
        <span key={key} className="glossary_highlighted" data-term={term || undefined}>
          {children}
        </span>
      );
    }

    // Anchor → hi-ui Link component
    if (tag === 'a') {
      let href = el.getAttribute('href') || '';
      const target = el.getAttribute('target');
      const isSupportUrl = /heartinternet\.uk\/support\//.test(href);

      // Strip query params from support article URLs (can't handle them)
      if (isSupportUrl) {
        href = href.split('?')[0];
      }

      // For support URLs, handle click directly to bypass GTM link decoration
      const cleanHref = href;
      const handleClick = isSupportUrl
        ? (e: React.MouseEvent) => {
            e.preventDefault();
            window.open(cleanHref, target === '_blank' ? '_blank' : '_self');
          }
        : undefined;

      return (
        <Link key={key} href={href} external={target === '_blank'} onClick={handleClick}>
          {children}
        </Link>
      );
    }

    // Build props from HTML attributes
    const props: Record<string, unknown> = { key };
    for (const attr of Array.from(el.attributes)) {
      if (attr.name === 'class') {
        props.className = attr.value;
      } else {
        props[attr.name] = attr.value;
      }
    }

    if (children.length === 0) {
      return React.createElement(tag, props);
    }
    return React.createElement(tag, props, ...children);
  }

  const result: React.ReactNode[] = [];
  root.childNodes.forEach((child) => {
    const converted = convertNode(child);
    if (converted !== null) result.push(converted);
  });

  return result;
}

export const TicketViewPage: React.FC<TicketViewPageProps> = ({
  id,
  title,
  status,
  created,
  updated,
  serviceDescription,
  subjectLabels,
  customerName = '',
  updates = [],
  feedbackQualityLevels = DEFAULT_FEEDBACK_LEVELS,
  onSubmitFeedback,
  ticketUrl = '/manage/ticket.cgi',
  glossaryDefinitions = {},
  className = '',
  style,
}) => {
  const [visibleBatches, setVisibleBatches] = useState(1);
  const [feedbackSent, setFeedbackSent] = useState<Record<string, boolean>>({});
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [fileError, setFileError] = useState('');
  const [fileName, setFileName] = useState('');
  const [attachmentErrors, setAttachmentErrors] = useState<Record<string, string>>({});
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const lastUpdateRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Filter to external updates only
  const externalUpdates = updates.filter((u) => u.updateType === 'external');
  const totalBatches = Math.ceil(externalUpdates.length / BATCH_SIZE);
  const hasMoreBatches = visibleBatches < totalBatches;

  // Find the last rateable update (only if not CLOSED-LOCKED)
  let lastRateableUpdateId: string | null = null;
  if (status !== 'CLOSED-LOCKED') {
    for (let i = externalUpdates.length - 1; i >= 0; i--) {
      const u = externalUpdates[i];
      if (u.text && !u.customerId) {
        if (u.feedbackQuality !== undefined && u.feedbackQuality !== null) {
          break; // Already rated — don't look further back
        }
        if (u.rateable) {
          lastRateableUpdateId = u.id;
          break;
        }
      }
    }
  }

  // Scroll to last update on mount
  useEffect(() => {
    const lastUpdateEl = document.getElementById('last-update');
    if (lastUpdateEl && typeof lastUpdateEl.scrollIntoView === 'function') {
      lastUpdateEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleShowMore = useCallback(() => {
    setVisibleBatches((prev) => prev + 1);
  }, []);

  const handleFeedbackSubmit = useCallback(
    async (updateId: string, rating: number) => {
      if (!onSubmitFeedback || feedbackSending) return;
      setFeedbackSending(true);
      try {
        const success = await onSubmitFeedback(updateId, rating);
        if (success) {
          setFeedbackSent((prev) => ({ ...prev, [updateId]: true }));
        }
      } finally {
        setFeedbackSending(false);
      }
    },
    [onSubmitFeedback, feedbackSending]
  );

  const handleCloseConfirm = useCallback(() => {
    const form = formRef.current!;
    const textarea = form.querySelector<HTMLTextAreaElement>('[name="update_text"]');
    if (textarea && !textarea.value.trim()) {
      textarea.value = 'Ticket closed with no reply.';
    }
    // Add the close button value as a hidden input (since we're not clicking the submit button)
    const closeInput = document.createElement('input');
    closeInput.type = 'hidden';
    closeInput.name = 'close';
    closeInput.value = 'Close Ticket';
    form.appendChild(closeInput);
    form.submit();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setFileError('');
      setFileName('');
      return;
    }
    const name = file.name.normalize('NFC');
    if (!ALLOWED_FILE_PATTERN.test(name)) {
      setFileError(
        'Only the following file types are allowed: .jpg, .jpeg, .png, .log, .txt, .doc, .docx, .pdf. ' +
          'Please also check for multiple extensions, double periods, periods in the name, or non-alphanumeric characters.'
      );
      e.target.value = '';
      setFileName('');
    } else {
      setFileError('');
      setFileName(name);
    }
  }, []);

  const handleDownloadAttachment = useCallback(
    async (e: React.MouseEvent, attachment: TicketUpdateAttachment) => {
      e.preventDefault();
      const { downloadUrl, originalFilename } = attachment;
      const sanitisedName = originalFilename.replace(/[^A-Za-z0-9._-]/g, '_');

      // Clear any previous error for this attachment
      setAttachmentErrors((prev) => {
        const next = { ...prev };
        delete next[downloadUrl];
        return next;
      });

      try {
        const response = await fetch(downloadUrl, {
          headers: { Accept: 'application/octet-stream' },
        });
        if (!response.ok) throw new Error(`Download returned ${response.status}`);
        const blob = await response.blob();
        const tempUrl = URL.createObjectURL(blob);
        const tempLink = document.createElement('a');
        tempLink.href = tempUrl;
        tempLink.download = sanitisedName;
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);
        URL.revokeObjectURL(tempUrl);
      } catch {
        setAttachmentErrors((prev) => ({
          ...prev,
          [downloadUrl]:
            'An error occurred when fetching the attachment file. If the problem persists, please contact support.',
        }));
      }
    },
    []
  );

  // Determine which updates are visible based on batching
  const visibleUpdateCount = visibleBatches * BATCH_SIZE;
  const hiddenCount = Math.max(0, externalUpdates.length - visibleUpdateCount);
  const visibleUpdates = externalUpdates.slice(hiddenCount);

  const classes = ['hi-ui-ticket-view-page', className].filter(Boolean).join(' ');

  if (!id) {
    return (
      <div className={classes} style={style}>
        <Text variant="heading-1">Ticket Details</Text>
        <Text variant="body">
          Sorry, that ticket does not exist. Please go back and re-load your ticket list.
        </Text>
        <Link href={`${ticketUrl}/`}>Back to ticket list</Link>
      </div>
    );
  }

  const serviceDisplay =
    subjectLabels && subjectLabels.length > 0 ? subjectLabels.join(', ') : serviceDescription || '';

  const statusBadgeVariant = STATUS_BADGE[status || ''] || ('neutral' as BadgeVariant);

  const metadataItems: DescriptionListItem[] = [
    { label: 'Ticket Number', value: id },
    { label: 'Created', value: created ? formatDateTime(created) : '' },
    { label: 'Updated', value: updated ? formatDateTime(updated) : '' },
    { label: 'Service(s)', value: serviceDisplay },
    { label: 'Summary', value: title || '' },
    {
      label: 'Status',
      value: (
        <Badge variant={statusBadgeVariant} pill>
          {status || ''}
        </Badge>
      ),
    },
  ];

  const renderStarRating = (updateId: string) => {
    if (feedbackSent[updateId]) {
      return (
        <div className="hi-ui-ticket-view-page__feedback-sent" role="status">
          <Text variant="body">Feedback sent, thank you!</Text>
        </div>
      );
    }

    const activeRating = hoverRating || selectedRating;

    return (
      <div className="hi-ui-ticket-view-page__rating" role="group" aria-label="Rate this response">
        <Text variant="body" bold>
          How would you rate our customer service agent&apos;s latest response?
        </Text>
        <div className="hi-ui-ticket-view-page__stars">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((value) => (
            <Tooltip
              key={value}
              content={feedbackQualityLevels[value] || `Rating ${value}`}
              position="top"
            >
              <button
                type="button"
                className={[
                  'hi-ui-ticket-view-page__star',
                  value <= activeRating ? 'hi-ui-ticket-view-page__star--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => {
                  setSelectedRating(value);
                  handleFeedbackSubmit(updateId, value);
                }}
                onMouseEnter={() => setHoverRating(value)}
                onMouseLeave={() => setHoverRating(0)}
                disabled={feedbackSending}
                aria-label={`Rate ${value} out of 9: ${feedbackQualityLevels[value] || ''}`}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="hi-ui-ticket-view-page__star-icon"
                  aria-hidden="true"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </button>
            </Tooltip>
          ))}
        </div>
        <Text variant="caption" className="hi-ui-ticket-view-page__rating-help">
          Rating our responses helps us improve customer service.
        </Text>
      </div>
    );
  };

  const renderUpdateContent = (update: TicketUpdate) => {
    return (
      <>
        {update.quote && (
          <div
            className={[
              'hi-ui-ticket-view-page__quote',
              update.quote.aborted ? 'hi-ui-ticket-view-page__quote--aborted' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <Text variant="body" bold>
              Quote for work: &pound;{(update.quote.amount || 0).toFixed(2)} for{' '}
              <em>{update.quote.work}</em>.{update.quote.paid && ' (Paid)'}
              {!update.quote.paid && !update.quote.aborted && update.quote.acceptUrl && (
                <>
                  {' '}
                  <Link href={update.quote.acceptUrl}>Click here to accept and check out</Link>.
                </>
              )}
            </Text>
          </div>
        )}

        {update.expertServices && update.expertServices.length > 0 && (
          <div className="hi-ui-ticket-view-page__expert-services">
            <Text variant="body" bold>
              You requested these services:
            </Text>
            <ul>
              {update.expertServices.map((service, i) => (
                <li key={i}>{service}</li>
              ))}
            </ul>
          </div>
        )}

        {update.text && (
          <div className="hi-ui-ticket-view-page__update-text">
            {update.textHtml ? (
              <Text as="div" variant="body">
                {parseHtmlWithGlossary(
                  update.textHtml.replace(/\xC2\xA0/g, ' ').replace(/\n/g, '<br />'),
                  glossaryDefinitions
                )}
              </Text>
            ) : (
              update.text.split('\n').map((line, i) => (
                <Text key={i} variant="body">
                  {line || '\u00a0'}
                </Text>
              ))
            )}
          </div>
        )}

        {update.salesSignatureText && (
          <div className="hi-ui-ticket-view-page__sales-signature">
            {parseHtmlWithGlossary(update.salesSignatureText, glossaryDefinitions)}
          </div>
        )}

        {update.text &&
          !update.customerId &&
          update.feedbackQuality !== undefined &&
          update.feedbackQuality > 0 && (
            <div className="hi-ui-ticket-view-page__rated">
              <Text variant="caption">
                You rated this response:{' '}
                <strong>
                  {feedbackQualityLevels[update.feedbackQuality] || `${update.feedbackQuality}/9`}
                </strong>
              </Text>
            </div>
          )}

        {update.attachment && (
          <div className="hi-ui-ticket-view-page__attachment">
            <Text variant="body" bold>
              Attachment:
            </Text>
            {update.attachment.stored ? (
              <>
                <Link
                  href="#"
                  onClick={(e) => handleDownloadAttachment(e, update.attachment!)}
                  className="hi-ui-ticket-view-page__attachment-link"
                >
                  {update.attachment.originalFilename}
                </Link>
                {attachmentErrors[update.attachment.downloadUrl] && (
                  <Text variant="error" className="hi-ui-ticket-view-page__attachment-error">
                    {attachmentErrors[update.attachment.downloadUrl]}
                  </Text>
                )}
              </>
            ) : (
              <Text variant="muted">{update.attachment.originalFilename} (deleted)</Text>
            )}
          </div>
        )}
      </>
    );
  };

  const renderUpdateHeader = (update: TicketUpdate) => {
    /* v8 ignore start */
    if (update.updateType !== 'external') return null;
    /* v8 ignore stop */
    if (!update.text && !update.status) return null;

    const authorName = update.customerId ? 'you' : update.updaterName || 'system';
    const proxyText = update.customerProxy ? `${update.customerProxy} on behalf of ` : '';

    return (
      <div
        className="hi-ui-ticket-view-page__update-header"
        {...(update.updatedNow ? { id: 'last-update' } : {})}
        ref={update.updatedNow ? lastUpdateRef : undefined}
      >
        <Text variant="caption">
          At {formatDateTime(update.datetime)}, {proxyText}
          {authorName}
          {update.status && (
            <>
              {' '}
              changed the ticket status to <strong>{update.status}</strong>
              {update.text ? ' and' : ''}
            </>
          )}
          {update.text && ' wrote:'}
        </Text>
      </div>
    );
  };

  return (
    <div className={classes} style={style}>
      <Text variant="heading-1">Ticket Details</Text>

      <DescriptionList items={metadataItems} layout="horizontal" striped />

      <Form
        ref={formRef}
        action={ticketUrl}
        method="post"
        encType="multipart/form-data"
        className="hi-ui-ticket-view-page__form"
      >
        <input type="hidden" name="ticket-id" value={id} />
        <input type="hidden" name="action" id="form-action" value="update" />

        {hasMoreBatches && (
          <div className="hi-ui-ticket-view-page__show-more">
            <Button type="button" variant="tertiary" onClick={handleShowMore}>
              View older entries ({hiddenCount} hidden)
            </Button>
          </div>
        )}

        <div className="hi-ui-ticket-view-page__updates">
          {visibleUpdates.map((update) => {
            const header = renderUpdateHeader(update);
            if (
              !header &&
              !update.quote &&
              !(update.expertServices && update.expertServices.length > 0)
            ) {
              return null;
            }

            const isStaff = !update.customerId;

            return (
              <div
                key={update.id}
                className={[
                  'hi-ui-ticket-view-page__update',
                  isStaff
                    ? 'hi-ui-ticket-view-page__update--staff'
                    : 'hi-ui-ticket-view-page__update--customer',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {header}
                <div className="hi-ui-ticket-view-page__update-body">
                  {renderUpdateContent(update)}
                </div>
              </div>
            );
          })}
        </div>

        {lastRateableUpdateId && renderStarRating(lastRateableUpdateId)}

        <div className="hi-ui-ticket-view-page__reply">
          {status === 'CLOSED-LOCKED' ? (
            <Text variant="body" bold>
              No further updates are possible on this ticket.
            </Text>
          ) : status !== 'CLOSED' ? (
            <>
              <FormField label="Your name:" labelPosition="left" id="tktnotname">
                <Input name="proxy" defaultValue={customerName} />
              </FormField>
              <Text variant="body" bold>
                To update this ticket with more information please enter the details below:
              </Text>
              <Textarea id="update-text" name="update_text" rows={10} fullWidth />
              <div className="hi-ui-ticket-view-page__file-upload">
                <Text variant="body" bold>
                  Optional File Attachment
                </Text>
                <Text variant="caption">
                  You can upload screenshots or accompanying documents as part of your support query
                  if it will assist us in resolving your issue.
                </Text>
                <Text variant="caption">
                  Allowed file types: .jpg, .jpeg, .png, .log, .txt, .doc, .docx, .pdf
                </Text>
                <div className="hi-ui-ticket-view-page__file-picker">
                  <input
                    type="file"
                    name="ticket-attachment"
                    id="ticket-attachment"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hi-ui-ticket-view-page__file-input-hidden"
                  />
                  <Button
                    type="button"
                    variant="tertiary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choose file
                  </Button>
                  <Text variant="caption" className="hi-ui-ticket-view-page__file-name">
                    {fileName || 'No file chosen'}
                  </Text>
                </div>
                {fileError && <Notice variant="error">{fileError}</Notice>}
                <Text variant="caption">Max file size supported: 1MB</Text>
              </div>
              <div className="hi-ui-ticket-view-page__actions">
                <Button type="submit" name="preserve" value="Update Ticket">
                  Update Ticket
                </Button>
                <Button type="button" variant="warning" onClick={() => setShowCloseConfirm(true)}>
                  Close Ticket
                </Button>
              </div>
            </>
          ) : (
            <>
              <Text variant="body" bold>
                If you are still experiencing the same problem originally described, you can reopen
                the ticket using the form below.
              </Text>
              <Textarea id="update-text" name="update_text" rows={10} fullWidth />
              <div className="hi-ui-ticket-view-page__file-upload">
                <Text variant="body" bold>
                  Optional File Attachment
                </Text>
                <Text variant="caption">
                  You can upload screenshots or accompanying documents as part of your support query
                  if it will assist us in resolving your issue.
                </Text>
                <Text variant="caption">
                  Allowed file types: .jpg, .jpeg, .png, .log, .txt, .doc, .docx, .pdf
                </Text>
                <div className="hi-ui-ticket-view-page__file-picker">
                  <input
                    type="file"
                    name="ticket-attachment"
                    id="ticket-attachment"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hi-ui-ticket-view-page__file-input-hidden"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choose file
                  </Button>
                  <Text variant="caption" className="hi-ui-ticket-view-page__file-name">
                    {fileName || 'No file chosen'}
                  </Text>
                </div>
                {fileError && <Notice variant="error">{fileError}</Notice>}
                <Text variant="caption">Max file size supported: 1MB</Text>
              </div>
              <FormField
                label={
                  <>
                    If you are not <strong>{customerName}</strong>, please fill in your name:
                  </>
                }
                labelPosition="left"
                id="tktnotname"
              >
                <Input name="proxy" />
              </FormField>
              <div className="hi-ui-ticket-view-page__actions">
                <Button type="submit" name="open" value="Update Ticket">
                  Re-open Ticket
                </Button>
              </div>
            </>
          )}
        </div>
      </Form>

      <ConfirmationDialog
        isOpen={showCloseConfirm}
        title="Close Ticket"
        confirmLabel="Close Ticket"
        cancelLabel="Cancel"
        confirmVariant="warning"
        onConfirm={handleCloseConfirm}
        onCancel={() => setShowCloseConfirm(false)}
      >
        <Text variant="body">
          Are you sure you want to close this ticket? You can reopen it later by submitting a new
          update.
        </Text>
      </ConfirmationDialog>

      <div className="hi-ui-ticket-view-page__back">
        <Link href={`${ticketUrl}/`}>Back to ticket list</Link>
      </div>
    </div>
  );
};

TicketViewPage.displayName = 'TicketViewPage';
